import jsep from 'jsep';
import { TpETLDryRunTableObject } from '../../interfaces';
import { VALTYPES } from '../../../../data/interfaces';
import { getETLTableObjectColumn } from '../../utils';
import { ARITY, etlExpressionFunctionList, TpETLExpressionFunctionDef } from './etlExpressionFunctions';

export const binaryOperators = ['+', '-', '*', '/', '==', '<', '<=', '>', '>=', '||', '&&'];

const functionsMap: { [id: string]: TpETLExpressionFunctionDef } = {};
for (const func of etlExpressionFunctionList) functionsMap[func.name] = func;

export function parseETLTableExpression(inputTableObj: TpETLDryRunTableObject, expressionString: string): any {
  const inputColIds = inputTableObj.columns.map((col) => col.identifier);

  function parseIdentifier(expr: jsep.Expression) {
    const idExpr = expr as jsep.Identifier;
    const colInfo = getETLTableObjectColumn(inputTableObj, idExpr.name);
    return {
      type: 'column',
      identifier: colInfo.identifier,
      dataType: colInfo.dataType,
      arity: ARITY.VECTOR,
    };
  }

  function parseUnaryOperator(expr: jsep.Expression) {
    const unExpr = expr as jsep.UnaryExpression;
    const argument = recursiveEval(unExpr.argument);
    if (unExpr.operator == '!') {
      if (argument.dataType !== VALTYPES.BOOLEAN) throw 'Not operator should be applied on a boolean';
      return {
        type: 'not',
        argument: recursiveEval(unExpr.argument),
        dataType: VALTYPES.BOOLEAN,
        arity: argument.arity,
      };
    }
    if (unExpr.operator == '-') {
      if (argument.dataType !== VALTYPES.NUMBER) throw 'Negative operator should be applied on a value';
      return {
        type: 'negative',
        argument: recursiveEval(unExpr.argument),
        dataType: VALTYPES.NUMBER,
        arity: argument.arity,
      };
    }
    throw `Unsupported unary operator ${unExpr.operator}`;
  }

  function parseLiteral(expr: jsep.Expression) {
    const litExpr = expr as jsep.Literal;
    return {
      type: typeof litExpr.value == 'number' ? 'literalNumber' : 'literalString',
      identifier: litExpr.value,
      dataType: typeof litExpr.value == 'number' ? VALTYPES.NUMBER : VALTYPES.STRING,
      arity: ARITY.SINGLE,
    };
  }

  function parseBinaryOperator(expr: jsep.Expression) {
    const binExpr = expr as jsep.BinaryExpression;
    const left = recursiveEval(binExpr.left);
    const right = recursiveEval(binExpr.right);
    if (!binaryOperators.includes(binExpr.operator)) throw `Unsupported binary operator ${binExpr.operator}`;
    let outputDataType = null;
    if (['&&', '||'].includes(binExpr.operator)) {
      if (left.dataType != VALTYPES.BOOLEAN || right.dataType != VALTYPES.BOOLEAN)
        throw `${binExpr.operator} operator expects boolean values`;
      outputDataType = VALTYPES.BOOLEAN;
    } else {
      if (left.dataType == VALTYPES.NUMBER && right.dataType == VALTYPES.NUMBER) {
        outputDataType = VALTYPES.NUMBER;
      } else if (binExpr.operator == '+') {
        outputDataType = left.dataType;
      }
      if (['<', '>', '<=', '>=', '=='].includes(binExpr.operator)) outputDataType = VALTYPES.BOOLEAN;
    }
    if (!outputDataType) throw `Incompatible arguments for ${binExpr.operator} operator`;
    return {
      type: 'binaryOperator',
      operator: binExpr.operator,
      left,
      right,
      dataType: outputDataType,
      arity: left.arity == ARITY.VECTOR || right.arity == ARITY.VECTOR ? ARITY.VECTOR : ARITY.SINGLE,
    };
  }

  function parseConditionalExpression(expr: jsep.Expression) {
    const condExpr = expr as jsep.ConditionalExpression;
    const test = recursiveEval(condExpr.test);
    if (test.dataType !== VALTYPES.BOOLEAN) throw 'Conditional expression argument should be a boolean';
    const consequent = recursiveEval(condExpr.consequent);
    const alternate = recursiveEval(condExpr.alternate);
    if (consequent.dataType !== alternate.dataType)
      throw 'Both alternatives of a conditional expression should be of the same data type';
    if (consequent.arity !== alternate.arity)
      throw 'Both alternatives of a conditional expression should be of the same arity';
    return {
      type: 'conditionalExpression',
      test,
      consequent,
      alternate,
      dataType: consequent.dataType,
      arity: consequent.arity,
    };
  }

  function parseFunction(expr: jsep.Expression) {
    const callExpr = expr as jsep.CallExpression;
    const functionName = String(callExpr.callee.name);
    const functionDef = functionsMap[functionName];
    if (!functionDef) throw `Invalid function "${functionName}"`;
    const argumentList = callExpr.arguments.map((arg) => recursiveEval(arg));

    try {
      const outputDef = functionDef.processArguments(argumentList);
      return {
        type: 'function',
        name: functionName,
        argumentList,
        dataType: outputDef.dataType,
        arity: outputDef.arity,
      };
    } catch (error) {
      throw `Error in function ${functionName}: ${String(error)}`;
    }
  }

  function recursiveEval(expr: jsep.Expression): any {
    if (expr.type == 'BinaryExpression') return parseBinaryOperator(expr);
    if (expr.type == 'UnaryExpression') return parseUnaryOperator(expr);
    if (expr.type == 'Identifier') return parseIdentifier(expr);
    if (expr.type == 'Literal') return parseLiteral(expr);
    if (expr.type == 'ConditionalExpression') return parseConditionalExpression(expr);
    if (expr.type == 'CallExpression') return parseFunction(expr);

    throw `Unsupported token type ${expr.type}`;
  }

  try {
    const parsedExpression = jsep(expressionString);
    return recursiveEval(parsedExpression);
  } catch (error) {
    throw `Invalid expression: ${String(error)}`;
  }
}
